import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import { ButtonLink } from '@commons/components/Button';
import { Link } from '@commons/components/Link';
import ShareLinks from '@commons/components/ShareLinks';
import Text from '@commons/components/Text';
import BaseContainer from '@commons/containers/BaseContainer';
import Flex from '@commons/containers/Flex';
import { generateId } from '@commons/helpers/string';
import PageBuilder from '@commons/pagebuilder';
import GatedInsightDrawer from '@commons/pagebuilder/webforms/GatedInsightDrawer';
import type { DrupalBlock, DrupalDocument, DrupalLink } from '@commons/types/drupal';

import type { InsightNodeProps } from '.';
import { InsightDownloadButton } from './DownloadButton';
import type { InsightSection } from './Sections';
import styles from './Sidebar.module.scss';

// Type definition for InsightSidebar block component properties.

type Props = DrupalBlock<{
  file?: DrupalDocument;
  link?: DrupalLink;
  sections: InsightSection[];
}>;

/**
 * InsightSidebar block component.
 *
 * Renders the left sidebar of an Insight page with the authors, share links, section links, and download button.
 */

const InsightSidebarBlock = ({ content }: Props) => {
  const t = useTranslations('commons');
  const tGatedInsight = useTranslations('forms.gatedInsight');

  const page = PageBuilder.getPage<InsightNodeProps>();

  if (!page) return null;

  const { file, link, sections = [] } = content;
  const { metaTags, customAuthor, memberAuthors = [], isGated } = page;

  const isWebinarPage = page.bundle === 'webinar';
  const hasAuthors = !isWebinarPage && (customAuthor || memberAuthors.length > 0);
  const authorCount = memberAuthors.length + (customAuthor ? 1 : 0);
  const renderedMemberAuthors = memberAuthors.map(PageBuilder.renderNode);

  const hasSections = !isWebinarPage && sections.length > 0;
  const renderedSectionLinks = sections.map((section) => {
    if (!section?.title) return null;

    const id = generateId(section.title);

    return (
      <Flex.Item border="top" paddingTop="xxs" key={id}>
        <Link level="p" href={`#${id}`} withArrow>
          {section.title}
        </Link>
      </Flex.Item>
    );
  });

  const webinarPage = page as { link?: DrupalLink; ctaLabel?: string };
  const webinarLink = isWebinarPage ? link || webinarPage.link : undefined;
  const watchNowLabel = webinarPage.ctaLabel || tGatedInsight('watchNow');

  return (
    <BaseContainer width={12} className={clsx(styles.sidebar, 'h-100')}>
      <Flex gap={{ xs: 'xs', lg: 'md' }} width={12} direction="column" className={styles.sticky}>
        {hasAuthors && (
          <Flex.Item>
            <Text color="grey-5" level="label-lg" transform="uppercase" marginBottom="xs">
              {t('author', { count: authorCount })}
            </Text>
            <Flex gap="xs" direction="column">
              {customAuthor && <Text marginBottom="none">{customAuthor}</Text>}
              {renderedMemberAuthors}
            </Flex>
          </Flex.Item>
        )}
        <Flex.Item>
          <Text color="grey-5" level="label-lg" transform="uppercase" marginBottom="xs">
            {t('share')}
          </Text>
          <ShareLinks
            links={['facebook', 'x', 'linkedin', 'email', 'pagelink']}
            emailSubject={metaTags.title}
            emailBody={metaTags.description}
          />
        </Flex.Item>
        {hasSections && (
          <Flex.Item className={styles.sectionLinks}>
            <Text color="grey-5" level="label-lg" transform="uppercase" marginBottom="xs">
              {t('jumpTo')}
            </Text>
            <Flex gap="xxs" border="bottom" direction="column" paddingBottom="xxs">
              {renderedSectionLinks}
            </Flex>
          </Flex.Item>
        )}
        {!isWebinarPage && file && <InsightDownloadButton file={file} isGated={isGated} />}
        {webinarLink?.url && !isGated && (
          <ButtonLink
            variant="primary"
            href={webinarLink.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {watchNowLabel}
          </ButtonLink>
        )}
        {webinarLink?.url && isGated && (
          <GatedInsightDrawer
            url={webinarLink.url}
            syncFormQueryParam
            resourceLabel={watchNowLabel}
            submitLabel={watchNowLabel}
            triggerProps={{ label: watchNowLabel, variant: 'primary' }}
          />
        )}
      </Flex>
    </BaseContainer>
  );
};

export default InsightSidebarBlock;
